import { computed, inject, Injector, signal } from "@angular/core";
import { CommonDateTimeField, CommonDateTimeFieldConfig, CommonForm, CommonMultiSelectField, CommonOptionsFieldConfig, CommonSelectField, CommonTextField } from "@eforall/common";
import { Demographics, PersonStatusFlags, Person, Races } from "@interfaces";

interface DemographicsFields {
	gender: string | undefined,
	genderOther: string | undefined,
	races: string[],
	hispanic: "N" | "Y" | "OptedOut" | undefined,
	birthday: number | undefined,
	veteran: "N" | "Y" | "OptedOut" | undefined,
	nativeEnglish: boolean | undefined,
	nativeSpanish: boolean | undefined,
}


export class PersonDemographicsForm {

	public readonly form = new CommonForm<DemographicsFields>(inject(Injector));
	private values = signal<Partial<Demographics>>({ gender: undefined, genderOther: undefined, races: [], hispanic: undefined, birthYear: undefined, birthDay: undefined, veteran: undefined, nativeEnglish: undefined, nativeSpanish: undefined });

	public personStatusFlags = signal<PersonStatusFlags>({ hasOptOutDemographics: false, isHandicapped: false, isDeceased: false });

	public setValues(person: Person) {
		this.form.resetAll();
		const demographics = person.demographics;
		if (!demographics) return;

		this.values.set({
			gender: demographics.gender,
			genderOther: demographics.genderOther,
			races: demographics.races,
			hispanic: demographics.hispanic,
			birthYear: demographics.birthYear,
			birthDay: demographics.birthDay,
			veteran: demographics.veteran,
			nativeEnglish: demographics.nativeEnglish,
			nativeSpanish: demographics.nativeSpanish,
		});

		this.personStatusFlags.set({
			hasOptOutDemographics: demographics.hasOptOutDemographics,
			isHandicapped: demographics.isHandicapped,
			isDeceased: demographics.isDeceased,
		})
	}

	updatePersonStatusFlags(flag: Partial<PersonStatusFlags>) {
		this.personStatusFlags.update(s => ({ ...s, ...flag }));
	}

	readonly gender = computed<CommonSelectField<string>>(() => ({
		config: signal<CommonOptionsFieldConfig<string>>({
			label: 'Gender',
			isRequired: false,
			options: [
				{ value: 'F', text: 'Female' },
				{ value: 'M', text: 'Male' },
				{ value: 'N', text: 'Non-Binary' },
				{ value: 'O', text: 'Other' },
				{ value: 'OptedOut', text: 'Prefer not to say' },
			],
		}),
		formFieldName: 'gender',
		form: this.form,
		actualValue: computed(() => this.values().gender || ''),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
	}));

	readonly genderOther = computed<CommonTextField>(() => ({
		config: signal({
			label: 'Other Gender',
			isRequired: false,
			min: 0,
			max: 45,
			fixCase: 'sentence-case',
			multiLine: false,
		}),
		formFieldName: 'genderOther',
		form: this.form,
		isExcluded: computed(() => {
			const genderField = this.gender();
			const pending = genderField.pendingValue();
			if (pending) {
				return pending !== 'O';
			} else {
				const actual = genderField.actualValue();
				return actual !== 'O';
			}
		}),
		actualValue: computed(() => this.values().genderOther || ''),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
	}));


	readonly races = computed<CommonMultiSelectField<string>>(() => ({
		config: signal<CommonOptionsFieldConfig<string>>({
			label: 'Race',
			isRequired: false,
			options: [
				{ value: Races.NativeAmerican, text: 'American Indian or Alaska Native' },
				{ value: Races.Asian, text: 'Asian' },
				{ value: Races.BlackOrAfricanAmerican, text: 'Black or African American' },
				{ value: Races.PacificIslander, text: 'Native Hawaiian or Other Pacific Islander' },
				{ value: Races.White, text: 'White' },
				{ value: Races.OptedOut, text: 'Prefer not to say', indepentSelect: true },
			],
		}),
		formFieldName: 'races',
		form: this.form,
		actualValue: computed(() => this.values().races || []),
		pendingValue: signal<string[] | null>(null),
		saving: signal(false),
		error: signal(''),
	}));

	readonly hispanicOrLatino = computed<CommonSelectField<"N" | "Y" | "OptedOut" | undefined>>(() => ({
		config: signal<CommonOptionsFieldConfig<"N" | "Y" | "OptedOut">>({
			label: 'Hispanic Or Latino',
			isRequired: false,
			options: [
				{ value: 'Y', text: 'Hispanic or Latino' },
				{ value: 'N', text: 'NOT Hispanic or Latino' },
				{ value: 'OptedOut', text: 'Prefer not to say' }
			],
		}),
		formFieldName: 'hispanic',
		form: this.form,
		actualValue: computed(() => this.values().hispanic),
		pendingValue: signal<"N" | "Y" | "OptedOut" | null>(null),
		saving: signal(false),
		error: signal(''),
	}));

	readonly birthday = computed<CommonDateTimeField>(() => ({
		config: signal<CommonDateTimeFieldConfig>({
			label: 'Birthday',
			isRequired: false,
			type: 'birthday',
			includeTime: false,
		}),
		formFieldName: 'birthday',
		form: this.form,
		actualValue: computed(() => {
			const values = this.values();
			const birthdayUTC = values && values.birthDay && values.birthYear ? new Date(
				values.birthYear,				// year e.g. 1900
				Math.floor(values.birthDay / 100) - 1,	// zero-based month e.g. 11 for December
				values.birthDay % 100,			// day of month
				12, 0, 0, 0					// noon
			).getTime() / 1000 : undefined;
			return birthdayUTC;
		}),
		pendingValue: signal<number | undefined | null>(null),
		saving: signal(false),
		error: signal(''),
	}));


	readonly veteran = computed<CommonSelectField<"N" | "Y" | "OptedOut" | undefined>>(() => ({
		config: signal<CommonOptionsFieldConfig<"N" | "Y" | "OptedOut">>({
			label: 'Veteran',
			isRequired: false,
			options: [
				{ value: 'Y', text: 'U.S. Veteran' },
				{ value: 'N', text: 'NOT a U.S. Veteran' },
				{ value: 'OptedOut', text: 'Prefer not to say' }
			],
		}),
		formFieldName: 'veteran',
		form: this.form,
		actualValue: computed(() => this.values().veteran),
		pendingValue: signal<"N" | "Y" | "OptedOut" | null>(null),
		saving: signal(false),
		error: signal(''),
	}));


	nativeSpeakerOptions(language: string) {
		return [
			{ value: true, text: `Native ${language} Speaker` },
			{ value: false, text: `NOT a Native ${language} Speaker` }
		]
	};

	readonly nativeEnglish = computed<CommonSelectField<boolean | undefined>>(() => ({
		config: signal<CommonOptionsFieldConfig<boolean>>({
			label: 'Native English Speaker',
			isRequired: false,
			options: this.nativeSpeakerOptions('English'),
		}),
		formFieldName: 'nativeEnglish',
		form: this.form,
		actualValue: computed(() => this.values().nativeEnglish),
		pendingValue: signal<boolean | null>(null),
		saving: signal(false),
		error: signal(''),
	}));

	readonly nativeSpanish = computed<CommonSelectField<boolean | undefined>>(() => ({
		config: signal<CommonOptionsFieldConfig<boolean>>({
			label: 'Native Spanish Speaker',
			isRequired: false,
			options: this.nativeSpeakerOptions('Spanish'),
		}),
		formFieldName: 'nativeSpanish',
		form: this.form,
		actualValue: computed(() => this.values().nativeSpanish),
		pendingValue: signal<boolean | null>(null),
		saving: signal(false),
		error: signal(''),
	}));

}